// @ts-nocheck
import React, { FC, useEffect, useState } from 'react';
import Theme from '../theme/theme';
import { IConfig } from '../../../../url-reader';
import {
  IUpdateMessagesPayload,
  updateMessages,
  selectIsPublicPostOpenedFromEditor,
  selectIsPopupClosed,
} from '../../modules/messages';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchForPosts,
  postsSelector,
  postsRelationshipsSelector,
  mergePostsWithCTAs,
  productsSelector,
  fetchNextPage,
} from '../../modules/posts';
import { MessageService } from '../../../../message-service';
import { IPost, IPost } from '../../../../shared/types/Post';
import { globalConfig } from '../../../../shared/config/config';
import useBanner from '../../hooks/use-banner';
import Banner from '../banner/banner';
import { IPersonalization } from '../../../../shared/types/Personalization';
import {
  ISocialFeedsGroupedItem,
  SocialFeedsTypesMap,
  mergePostsWithSocialFeedType,
} from '../../../../shared/mappers/map-social-feeds';
import { ITintMeta } from '../../../../shared/services/tint.service';
import { IQueryParamFilters } from '../../../../shared/services/api.service';
import { Nullable } from '../../../../shared/types/Typescript-utils';

interface IThemeWrapper {
  theme: {
    personalization: IPersonalization;
    socialFeeds: SocialFeedsTypesMap<readonly ISocialFeedsGroupedItem[]>;
    meta: ITintMeta;
    config: IConfig;
    customFontSecondary?: Nullable<string>;
    customFontPopup?: Nullable<string>;
  };
}

const ThemeWrapper: FC<IThemeWrapper> = ({ theme }) => {
  const posts = useSelector(postsSelector);
  const products = useSelector(productsSelector);
  const postsRelationships = useSelector(postsRelationshipsSelector);
  // @ts-ignore
  const postsWithSocialFeedType: IPost[] = mergePostsWithSocialFeedType(posts, theme.socialFeeds);
  const postsWithCTA: readonly IPost[] = mergePostsWithCTAs(postsWithSocialFeedType, postsRelationships);
  const {
    personalization: {
      attributes: {
        data: {
          publicpost_button_text_color_disabled: publicpostButtonTextColorDisabled,
          publicpost_button_text_color: publicpostButtonTextColor,
          publicpost_button_color_disabled: publicpostButtonColorDisabled,
          color_namebar_buttons: colorNamebarButtons,
          publicpost_button_color: publicpostButtonColor,
          waterfallToggle,
          public_add_post: publicPost,
          publicpost_button_text: publicpostButtonText,
          banner: bannerData,
          remove_duplicate_images: removeDuplicateImages,
        },
      },
    },
    config: { tags },
  } = theme;

  const addPostStyles = {
    color: publicpostButtonTextColorDisabled ? globalConfig.colorNamebarButtonsTextColor : publicpostButtonTextColor,
    background: publicpostButtonColorDisabled
      ? colorNamebarButtons || globalConfig.colorNamebarButtonsBackground
      : publicpostButtonColor,
  };

  const banner = useBanner(bannerData);

  const navSearchColor = colorNamebarButtons ? colorNamebarButtons : '';
  const navItemColor = colorNamebarButtons ? colorNamebarButtons : globalConfig.colorNamebarButtonsBackground;
  const addPostText = publicpostButtonText ? publicpostButtonText : globalConfig.defaultPublicPostPlaceholders.title;
  const addReviewText = globalConfig.defaultReviewPlaceholders.title;

  const dispatch = useDispatch();

  const isPublicPostOpenedFromEditor = useSelector(selectIsPublicPostOpenedFromEditor);

  const isPopupClosed = useSelector(selectIsPopupClosed);
  const setIsPopupClosed = (payload: IUpdateMessagesPayload) => dispatch(updateMessages(payload));

  const [active, setActive] = useState(document.activeElement as HTMLInputElement);

  const handleEnterKey = (event: React.KeyboardEvent<HTMLDivElement>, callback: () => void) => {
    if (event.keyCode === 13 || event.key === 'Enter') {
      setActive(document.activeElement as HTMLInputElement);
      callback();
    }
  };

  useEffect(() => {
    if (isPopupClosed && active.classList.contains('add-post')) {
      active.focus();
      setIsPopupClosed({ key: 'isPopupClosed', value: false });
    }
  }, [isPopupClosed]);

  useEffect(() => {
    if (isPublicPostOpenedFromEditor) {
      MessageService.send({
        type: globalConfig.postMessage.actions.openPublicPost,
        personalization: theme.personalization,
        customFontPopup: theme.customFontPopup,
        socialFeeds: theme.socialFeeds,
        config: theme.config,
        meta: theme.meta,
      });

      handleSetIsPublicPostOpenedFromEditor({ key: 'isPublicPostOpenedFromEditor', value: false });
    }
  }, [isPublicPostOpenedFromEditor]);

  const handleSetIsPublicPostOpenedFromEditor = (payload: IUpdateMessagesPayload) => dispatch(updateMessages(payload));
  const handleOnNavItemClick = (payload: IQueryParamFilters) =>
    dispatch(searchForPosts(payload, removeDuplicateImages));
  const handleOnNavSearch = (payload: IQueryParamFilters) =>
    dispatch(
      searchForPosts(
        payload,
        removeDuplicateImages,
        theme.personalization.attributes.theme === 'table' ? ',replies' : undefined
      )
    );

  const fetchNextPagePosts = (isPagination: boolean) => dispatch(fetchNextPage(isPagination));

  const addPost = () =>
    MessageService.send({
      type: globalConfig.postMessage.actions.openPublicPost,
      personalization: theme.personalization,
      socialFeeds: theme.socialFeeds,
      config: theme.config,
      meta: theme.meta,
    });

  const addReview = () => {
    MessageService.send({
      type: globalConfig.postMessage.actions.openReview,
      personalization: theme.personalization,
      socialFeeds: theme.socialFeeds,
      config: theme.config,
      meta: theme.meta,
    });
  };

  return (
    <>
      <Banner banner={banner} />
      <Theme
        theme={theme}
        banner={banner}
        posts={postsWithCTA}
        fetchNextPage={fetchNextPagePosts}
        products={products}
        navConfig={{
          publicPost,
          waterfallToggle,
          handleOnNavItemClick,
          navItemColor,
          handleOnNavSearch,
          navSearchColor,
          tags,
        }}
        addPostConfig={{
          text: addPostText,
          style: addPostStyles,
          onClick: addPost,
          handleEnterKey: e => handleEnterKey(e, addPost),
        }}
        addReviewConfig={{
          text: addReviewText,
          onClick: addReview,
          handleEnterKey: e => handleEnterKey(e, addReview),
        }}
      ></Theme>
    </>
  );
};

export default ThemeWrapper;
